import React, { useState, useContext } from "react";
import { 
  makeStyles, 
  Paper, 
  Typography, 
  Grid, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Tabs, 
  Tab, 
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  useTheme
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ChatIcon from "@material-ui/icons/Chat";
import PeopleIcon from "@material-ui/icons/People";
import SettingsIcon from "@material-ui/icons/Settings";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeIcon from "@material-ui/icons/Code";
import LiveHelpIcon from "@material-ui/icons/LiveHelp";
import StarsIcon from "@material-ui/icons/Stars";
import AssessmentIcon from "@material-ui/icons/Assessment";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";

import ColorModeContext from "../../layout/themeContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(4),
    paddingBottom: 100,
    backgroundColor: theme.palette.background.default,
  },
  header: {
    marginBottom: theme.spacing(4),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: theme.palette.primary.main,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  tabs: {
    marginBottom: theme.spacing(4),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiTab-wrapper": {
      fontWeight: "bold",
    },
  },
  sectionTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    borderRadius: "8px !important",
    backgroundColor: theme.palette.background.paper,
    "&:before": {
      display: "none",
    },
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  accordionSummary: {
    backgroundColor: theme.palette.type === "light" ? "#f8f9fa" : "rgba(255,255,255,0.05)",
    borderRadius: "8px",
    "&.Mui-expanded": {
      backgroundColor: theme.palette.type === "light" ? "rgba(101, 23, 171, 0.05)" : "rgba(101, 23, 171, 0.2)",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: "12px",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 8px 16px ${theme.palette.type === "light" ? "rgba(101, 23, 171, 0.1)" : "rgba(0,0,0,0.4)"}`,
    },
  },
  preCode: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    padding: theme.spacing(2),
    borderRadius: "8px",
    overflowX: "auto",
    fontSize: "14px",
    fontFamily: "'Fira Code', monospace",
    marginTop: theme.spacing(1),
  },
  methodBadge: {
    padding: "4px 8px",
    borderRadius: "4px",
    fontWeight: "bold",
    marginRight: theme.spacing(1),
    fontSize: "12px",
    color: "#fff",
  },
  post: { backgroundColor: "#4caf50" },
  get: { backgroundColor: "#2196f3" },
  put: { backgroundColor: "#ff9800" },
  delete: { backgroundColor: "#f44336" },
  stepNumber: {
    display: "inline-block",
    width: 24,
    height: 24,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "50%",
    textAlign: "center",
    lineHeight: "24px",
    marginRight: theme.spacing(1),
    fontSize: "14px",
    fontWeight: "bold",
  }
}));

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box pt={3}>{children}</Box>}
    </div>
  );
};

const DocumentationPortal = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const [tabValue, setTabValue] = useState(0);
  const [language, setLanguage] = useState("React");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getCodeExample = (method, url, body = null) => {
    switch (language) {
      case "React":
        return `fetch('${url}', {
  method: '${method}',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer {token}'
  },
  ${body ? `body: JSON.stringify(${JSON.stringify(body, null, 2)})` : ""}
})
.then(r => r.json())
.then(d => console.log(d));`;
      case "Javascript":
        return `const response = await fetch('${url}', {
  method: '${method}',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer {token}'
  },
  ${body ? `body: JSON.stringify(${JSON.stringify(body, null, 2)})` : ""}
});
const data = await response.json();`;
      case "Python":
        return `import requests
url = '${url}'
headers = {'Authorization': 'Bearer {token}', 'Content-Type': 'application/json'}
${body ? `data = ${JSON.stringify(body, null, 2)}` : ""}
response = requests.${method.toLowerCase()}(url, headers=headers${body ? ", json=data" : ""})
print(response.json())`;
      case "PHP":
        return `<?php
$url = '${url}';
$headers = ['Authorization: Bearer {token}', 'Content-Type: application/json'];
$options = [
  'http' => [
    'header'  => implode("\\r\\n", $headers),
    'method'  => '${method}',
    ${body ? `'content' => json_encode(${JSON.stringify(body, null, 2)})` : ""}
  ]
];
$context  = stream_context_create($options);
echo file_get_contents($url, false, $context);`;
      default:
        return `// Exemplo não disponível para ${language}`;
    }
  };

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <StarsIcon style={{ fontSize: 40 }} />
          <div>
            <Typography variant="h4" style={{ fontWeight: "bold" }}>Manual do Usuário OrionChat</Typography>
            <Typography variant="body1">O guia definitivo para gerenciar sua comunicação multicanal</Typography>
          </div>
        </div>
      </div>


      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Guia do Operador" icon={<ChatIcon />} />
        <Tab label="Configurações & Gestão" icon={<SettingsIcon />} />
        <Tab label="Relatórios & API" icon={<AssessmentIcon />} />
      </Tabs>

      {/* --- OPERATOR TAB --- */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <ChatIcon /> 1. Comunicação & Atendimento
            </Typography>
            <Typography variant="body1" paragraph>
              Este é o módulo onde o trabalho acontece. Aqui você lida com os clientes em tempo real.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Gestão de Tickets (Atendimentos)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <Typography variant="body2" paragraph>
                    <b>Aguardando:</b> Conversas novas que nenhum operador atendeu. Clique em "Aceitar" para assumir o controle.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <b>Abertos:</b> Seus tickets ativos. Aqui você pode enviar textos, áudios, anexos e emojis.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <b>Resolvidos:</b> Histórico de conversas finalizadas. Você pode reabrir um ticket se necessário.
                  </Typography>
                  <Typography variant="body2">
                    <b>Dica:</b> Use o botão de <b>Notas</b> para deixar anotações internas no ticket que outros operadores podem ver.
                  </Typography>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Transferência & Filas</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Você pode transferir um ticket para outra <b>Fila</b> (ex: Vendas p/ Suporte) ou para um <b>Usuário</b> específico. Basta clicar no ícone de aspas/seta no ticket e escolher o destino.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Respostas Rápidas (/) </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Crie atalhos para textos que você envia com frequência. Para usar, basta digitar barra <b>/</b> no campo de mensagem e selecionar a resposta desejada. Isso garante agilidade e padronização.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Agendamentos</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Precisa enviar uma mensagem para um cliente amanhã às 09h? Use o menu <b>Agendamentos</b>. Você define o contato, o texto, o arquivo (opcional) e a data exata do envio.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <ViewKanbanIcon /> 2. Ferramentas de Produtividade
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6" gutterBottom><ViewKanbanIcon style={{ verticalAlign: "middle" }} /> Kanban</Typography>
              <Typography variant="body2">
                Visualize seus atendimentos em um funil. Mova os clientes entre as etapas de "Prospecção", "Venda" ou "Finalizado" apenas arrastando os cards.
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6" gutterBottom><ScheduleIcon style={{ verticalAlign: "middle" }} /> Tarefas (To-Do)</Typography>
              <Typography variant="body2">
                Uma lista de afazeres interna para não esquecer compromissos. Adicione tarefas, marque como concluídas e organize seu dia.
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6" gutterBottom><ChatIcon style={{ verticalAlign: "middle" }} /> Chats Internos</Typography>
              <Typography variant="body2">
                Comunique-se com outros membros da equipe sem sair do OrionChat. Crie grupos ou envie mensagens privadas.
              </Typography>
            </div>
          </Grid>
        </Grid>
      </TabPanel>

      {/* --- ADMIN TAB --- */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <SettingsIcon /> 3. Configurações Administrativas
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Conexões (WhatsApp)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <Typography variant="body2" paragraph>
                    <span className={classes.stepNumber}>1</span> Clique em <b>ADICIONAR WHATSAPP</b>.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <span className={classes.stepNumber}>2</span> Escolha um nome para a conexão e salve.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <span className={classes.stepNumber}>3</span> Clique em <b>QR CODE</b> e escaneie o código com seu celular (Aparelhos Conectados {" > "} Conectar Aparelho).
                  </Typography>
                  <Typography variant="body2">
                    <b>Atenção:</b> Você pode gerenciar mensagens automáticas e horários de funcionamento específicos por conexão.
                  </Typography>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Usuários & Permissões</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Adicione sua equipe e defina o perfil: <b>Admin</b> (acesso total), <b>Supervisor</b> (acesso aos relatórios e chats em tempo real) ou <b>User</b> (apenas atendimento).
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={6}>
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Filas & Chatbot</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  Organize seus setores. Ao criar uma <b>Fila</b>, você pode definir as opções do menu inicial que o cliente recebe. Você pode criar sub-opções para criar um fluxo de atendimento automático simples.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                <Typography style={{ fontWeight: "bold" }}>Financeiro & Assinatura</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  No menu <b>Financeiro</b>, acompanhe suas faturas e o status do seu plano. Você pode realizar o pagamento via PIX para manter seu acesso sempre ativo.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <TrendingUpIcon /> 4. Campanhas & Automação Avançada
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className={classes.card}>
              <Typography variant="h6" gutterBottom><FlashOnIcon style={{ verticalAlign: "middle" }} /> Campanhas e Disparos</Typography>
              <Typography variant="body2" paragraph>
                <b>Envio Rápido:</b> Para disparos imediatos para uma lista colada de números.
              </Typography>
              <Typography variant="body2" paragraph>
                <b>Lista de Contatos:</b> Crie grupos de clientes importando planilhas CSV para campanhas recorrentes.
              </Typography>
              <Typography variant="body2">
                <b>Configuração:</b> Defina o intervalo randômico de segundos entre as mensagens para evitar bloqueios do WhatsApp.
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12} md={6}>
            <div className={classes.card}>
              <Typography variant="h6" gutterBottom><AccountTreeIcon style={{ verticalAlign: "middle" }} /> FlowBuilder (O Cérebro)</Typography>
              <Typography variant="body2">
                Uma interface visual para criar automações complexas. Você pode criar caminhos baseados na resposta do usuário, coletar dados, integrar com APIs externas e direcionar conversas de inteligência artificial.
              </Typography>
              <Box mt={2} bgcolor="rgba(101, 23, 171, 0.05)" p={1} borderRadius={4}>
                <Typography variant="caption">Dica: Use o nó "Condição" para criar caminhos diferentes para clientes novos e antigos.</Typography>
              </Box>
            </div>
          </Grid>
        </Grid>
      </TabPanel>

      {/* --- REPORTS & API TAB --- */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <AssessmentIcon /> 5. Inteligência de Dados & Relatórios
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6">Relatórios de Atendimento</Typography>
              <List>
                <ListItem dense><ListItemText primary="Tempo Médio de Atendimento" /></ListItem>
                <ListItem dense><ListItemText primary="Performance por Usuário" /></ListItem>
                <ListItem dense><ListItemText primary="Quantidade de Mensagens Enviadas/Recebidas" /></ListItem>
              </List>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6">Avaliações NPS</Typography>
              <Typography variant="body2">
                Veja as notas que seus clientes dão ao final de cada atendimento. Meça a satisfação da sua audiência em tempo real.
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className={classes.card}>
              <Typography variant="h6">Chats em Tempo Real</Typography>
              <Typography variant="body2">
                Ideal para gerentes. Visualize o que está acontecendo em todos os atendimentos ativos simultaneamente (Módulo Moments).
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <CodeIcon /> 6. Documentação Técnica (API)
            </Typography>
            <Typography variant="body1" paragraph>
              Integre o OrionChat com seu próprio sistema ou CRM. Abaixo você encontra todos os endpoints disponíveis.
            </Typography>
            
            <Paper variant="outlined" style={{ padding: 20, backgroundColor: theme.palette.background.paper }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" variant="outlined" style={{ marginBottom: 20 }}>
                    <InputLabel>Selecione a Linguagem</InputLabel>
                    <Select value={language} onChange={(e) => setLanguage(e.target.value)} label="Selecione a Linguagem">
                      <MenuItem value="React">React / Axios</MenuItem>
                      <MenuItem value="Javascript">Node.js / Fetch</MenuItem>
                      <MenuItem value="Python">Python / Requests</MenuItem>
                      <MenuItem value="PHP">PHP / File_Get_Contents</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  {/* --- AUTH --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>1. Autenticação</Typography>
                  <Typography variant="subtitle2">POST /auth/login</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/auth/login", {
                      username: "admin",
                      password: "password"
                    })}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- TICKETS --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>2. Tickets</Typography>
                  <Typography variant="subtitle2">GET /ticket/list</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.get}`}>GET</span>
                    {getCodeExample("GET", "https://api.orionchat.com.br/api/ticket/list")}
                  </pre>
                  <Typography variant="subtitle2" style={{ marginTop: 10 }}>POST /ticket/create</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/ticket/create", {
                      title: "Dúvida sobre plano",
                      contactId: 1
                    })}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- MESSAGES --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>3. Mensagens</Typography>
                  <Typography variant="subtitle2">POST /message/send</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/messages/send", {
                      number: "5511999998888",
                      body: "Olá, como posso ajudar?",
                      whatsappId: 1
                    })}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- CONTACTS --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>4. Contatos</Typography>
                  <Typography variant="subtitle2">GET /contact/list</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.get}`}>GET</span>
                    {getCodeExample("GET", "https://api.orionchat.com.br/api/contact/list")}
                  </pre>
                  <Typography variant="subtitle2" style={{ marginTop: 10 }}>POST /contact/create</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/contact/create", {
                      name: "Cliente Exemplo",
                      number: "5511999990000"
                    })}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- INVOICES --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>5. Faturas</Typography>
                  <Typography variant="subtitle2">GET /invoice/list</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.get}`}>GET</span>
                    {getCodeExample("GET", "https://api.orionchat.com.br/api/invoice/list")}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- SCHEDULES --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>6. Agendamentos</Typography>
                  <Typography variant="subtitle2">POST /schedules</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/schedules", {
                      body: "Mensagem agendada",
                      sendAt: "2026-04-01T10:00:00Z",
                      contactId: 1
                    })}
                  </pre>
                  <Divider style={{ margin: "20px 0" }} />

                  {/* --- WEBHOOK --- */}
                  <Typography variant="h6" style={{ color: theme.palette.primary.main }}>7. Webhook</Typography>
                  <Typography variant="subtitle2">POST /webhook</Typography>
                  <pre className={classes.preCode}>
                    <span className={`${classes.methodBadge} ${classes.post}`}>POST</span>
                    {getCodeExample("POST", "https://api.orionchat.com.br/api/webhook", {
                      url: "https://seu-sistema.com/webhook",
                      events: ["message:received"]
                    })}
                  </pre>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <Box mt={6} display="flex" justifyContent="center">
        <Chip 
          icon={<VerifiedUserIcon style={{ color: "white" }} />} 
          label="Documentação Oficial OrionChat - Sistema Completo" 
          style={{ backgroundColor: theme.palette.primary.main, color: "white", padding: "20px" }} 
        />
      </Box>
    </Paper>
  );
};

export default DocumentationPortal;